/**
 * InlineFindingsCarousel
 *
 * Renders the unacted Scope-Intelligence findings surfaced inline under a
 * conversation message. When more than one finding is present we show them one
 * at a time with a Prev / "N of M" / Next pager — mirroring the interactive
 * question (MCQ) card — instead of stacking every card vertically.
 *
 * As the user acts on a finding it's removed from `findings` upstream (it moves
 * to the Scope Intelligence panel). Keeping the same index then naturally
 * reveals the next finding; the effect clamps the index when the array shrinks
 * past the end.
 *
 * @module @ideoz/ui/components/molecules/InlineFindingsCarousel
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Finding, FindingAction } from '@ideoz/core';
import { FindingCard } from './FindingCard';
import { cn } from '../../lib/utils';

export interface InlineFindingsCarouselProps {
  findings: Finding[];
  onAction: (findingId: string, action: FindingAction) => void;
  /** Id of the finding currently resolving — only that card's actions disable. */
  resolvingFindingId?: string | null;
  className?: string;
  testId?: string;
}

export const InlineFindingsCarousel: React.FC<InlineFindingsCarouselProps> = ({
  findings,
  onAction,
  resolvingFindingId = null,
  className,
  testId = 'inline-findings-carousel',
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const PrevIcon = isRtl ? ChevronRight : ChevronLeft;
  const NextIcon = isRtl ? ChevronLeft : ChevronRight;
  const [index, setIndex] = useState(0);
  const total = findings.length;

  // Acting on a finding removes it upstream, shrinking the array. Clamp so the
  // index never points past the end (staying put shows the next finding).
  useEffect(() => {
    setIndex((i) => Math.min(i, Math.max(0, total - 1)));
  }, [total]);

  if (total === 0) return null;

  const safeIndex = Math.min(index, total - 1);
  const finding = findings[safeIndex];

  return (
    <div className={cn('flex flex-col gap-1.5', className)} data-testid={testId}>
      {total > 1 && (
        <div className="flex items-center justify-end gap-1 px-1 text-sm text-gray-500 dark:text-gray-400">
          <button
            type="button"
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={safeIndex === 0}
            aria-label={t('scopeIntel.prevFinding')}
            className="rounded p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 dark:hover:text-gray-200"
          >
            <PrevIcon size={18} />
          </button>
          <span className="tabular-nums" data-testid="inline-findings-pager">
            {t('scopeIntel.pager', { current: safeIndex + 1, total })}
          </span>
          <button
            type="button"
            onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
            disabled={safeIndex === total - 1}
            aria-label={t('scopeIntel.nextFinding')}
            className="rounded p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 dark:hover:text-gray-200"
          >
            <NextIcon size={18} />
          </button>
        </div>
      )}
      <FindingCard
        key={finding.id}
        finding={finding}
        onAction={onAction}
        isResolving={resolvingFindingId === finding.id}
      />
    </div>
  );
};

export default InlineFindingsCarousel;
